import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const avatarVariants = cva(
  'relative inline-flex items-center justify-center rounded-full shrink-0 overflow-hidden',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-[10px]',
        sm: 'h-8 w-8 text-xs',
        default: 'h-10 w-10 text-sm',
        lg: 'h-12 w-12 text-base',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  name?: string;
  src?: string;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size, name, src, ...props }, ref) => {
    const [imgError, setImgError] = React.useState(false);

    const fallbackLetter = name ? name.charAt(0).toUpperCase() : '?';

    const showImage = src && !imgError;

    return (
      <div
        ref={ref}
        className={cn(avatarVariants({ size, className }))}
        {...props}
      >
        {showImage ? (
          <img
            src={src}
            alt={name ?? 'Avatar'}
            className="h-full w-full rounded-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-raios-accent to-purple-600 font-medium text-white">
            {fallbackLetter}
          </div>
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

// eslint-disable-next-line react-refresh/only-export-components
export { Avatar, avatarVariants };
export type { AvatarProps };
